import { useState } from 'react';
import { formatDate } from '@/lib/utils';
import {
  useRequestVolume,
  useAeTurnaround,
  useAeEffectiveness,
  useExceptions,
  useExpiringHolds,
  useVolumeBreakdown,
} from '@/hooks/useAnalytics';
import { KpiCard } from '@/components/analytics/KpiCard';
import { DrillDownTable, type ColumnDef } from '@/components/analytics/DrillDownTable';
import { ChartCard } from '@/components/analytics/ChartCard';
import { LineChartWrapper } from '@/components/analytics/LineChartWrapper';
import { BarChartWrapper } from '@/components/analytics/BarChartWrapper';
import { DonutChartWrapper } from '@/components/analytics/DonutChartWrapper';
import type {
  AnalyticsFilters,
  AeMetrics,
  ExceptionDetail,
  ExpiringHoldItem,
} from '@/types';

interface SalesViewProps {
  filters: AnalyticsFilters;
}

/** Section heading style per OUTFRONT design system */
const SECTION_HEADING = 'text-[16px] font-semibold uppercase tracking-[0.05em] text-[#111111]';

/** Region colors for donut chart */
const REGION_COLORS: Record<string, string> = {
  West: '#6B4FBB',
  Central: '#1A7A3C',
  East: '#92400E',
};

// ─── Section 1: Request Volume (Metric 2.1) ────────────────────────────────

function RequestVolumeSection({ filters }: { filters: AnalyticsFilters }) {
  const { data, isLoading, error } = useRequestVolume(filters);

  // Merge current and previous period data for the line chart overlay.
  // Align by day index: day 0 of current period pairs with day 0 of previous period.
  // Generate a complete date sequence for each period to zero-fill gaps where the
  // backend omits days with no data, preventing index misalignment.
  const chartData = (() => {
    if (!data) return [];

    const currentMap = new Map(data.currentPeriod.map(d => [d.date, d.count]));
    const previousMap = new Map(data.previousPeriod.map(d => [d.date, d.count]));

    // Generate contiguous date range from filters
    const start = new Date(filters.startDate);
    const end = new Date(filters.endDate);
    const periodDays = Math.round((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1;
    const prevEnd = new Date(start.getTime() - 1000 * 60 * 60 * 24); // day before current start

    const merged: Record<string, unknown>[] = [];
    for (let i = 0; i < periodDays; i++) {
      const currentDate = new Date(start.getTime() + i * 1000 * 60 * 60 * 24);
      const previousDate = new Date(prevEnd.getTime() - (periodDays - 1 - i) * 1000 * 60 * 60 * 24);
      const currentKey = currentDate.toISOString().split('T')[0] ?? '';
      const previousKey = previousDate.toISOString().split('T')[0] ?? '';

      merged.push({
        date: currentKey,
        'Current Period': currentMap.get(currentKey) ?? 0,
        'Previous Period': previousMap.get(previousKey) ?? 0,
      });
    }
    return merged;
  })();

  const changeColor = (() => {
    if (data?.changePercent == null) return 'text-[#555555]';
    if (data.changePercent > 0) return 'text-[#1A7A3C]';
    if (data.changePercent < 0) return 'text-[#B91C1C]';
    return 'text-[#555555]';
  })();

  return (
    <section className="mb-8">
      <h2 className={SECTION_HEADING}>Request Volume</h2>
      <div className="mt-4">
        <ChartCard
          title="Request Volume Trend"
          loading={isLoading}
          error={error ? 'Failed to load request volume data' : null}
        >
          {chartData.length > 0 ? (
            <LineChartWrapper
              data={chartData}
              xAxisKey="date"
              series={[
                { dataKey: 'Current Period', name: 'Current Period', color: '#6B4FBB' },
                { dataKey: 'Previous Period', name: 'Previous Period', color: '#9A9A9A', strokeDasharray: '4 4', dot: false },
              ]}
              yAxisLabel="Count"
            />
          ) : (
            <div className="flex items-center justify-center h-[300px]">
              <p className="text-sm text-[#555555]">No data available</p>
            </div>
          )}
        </ChartCard>
      </div>
      <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-6">
        <KpiCard
          label="Total Current"
          value={data?.totalCurrent ?? null}
          loading={isLoading}
          error={error ? 'Failed to load data' : null}
        />
        <KpiCard
          label="Total Previous"
          value={data?.totalPrevious ?? null}
          loading={isLoading}
          error={error ? 'Failed to load data' : null}
        />
        <KpiCard
          label="Change %"
          value={data?.changePercent != null ? `${data.changePercent > 0 ? '+' : ''}${data.changePercent.toFixed(1)}` : null}
          unit="%"
          loading={isLoading}
          error={error ? 'Failed to load data' : null}
          className={data?.changePercent != null ? changeColor : undefined}
        />
      </div>
    </section>
  );
}

// ─── Section 2: AE Turnaround (Metric 2.2) ─────────────────────────────────

function AeTurnaroundSection({ filters }: { filters: AnalyticsFilters }) {
  const { data, isLoading, error } = useAeTurnaround(filters);

  const chartData = (data?.byRegion ?? []).map((r) => ({
    region: r.region,
    meanBusinessDays: r.meanBusinessDays ?? 0,
  }));

  return (
    <section className="mb-8">
      <h2 className={SECTION_HEADING}>AE Turnaround</h2>
      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <KpiCard
          label="Overall Mean Turnaround"
          value={data?.overallMeanBusinessDays != null ? data.overallMeanBusinessDays.toFixed(1) : null}
          unit="days"
          loading={isLoading}
          error={error ? 'Failed to load AE turnaround data' : null}
        />
      </div>
      <div className="mt-6">
        <ChartCard
          title="Mean Turnaround by Region"
          loading={isLoading}
          error={error ? 'Failed to load turnaround by region' : null}
        >
          {chartData.length > 0 ? (
            <BarChartWrapper
              data={chartData as Record<string, unknown>[]}
              categoryKey="region"
              series={[
                { dataKey: 'meanBusinessDays', name: 'Mean Business Days', color: '#6B4FBB' },
              ]}
              yAxisLabel="Business Days"
            />
          ) : (
            <div className="flex items-center justify-center h-[300px]">
              <p className="text-sm text-[#555555]">No data available</p>
            </div>
          )}
        </ChartCard>
      </div>
    </section>
  );
}

// ─── Section 3: AE Effectiveness (Metric 2.3) ──────────────────────────────

function AeEffectivenessSection({ filters }: { filters: AnalyticsFilters }) {
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [sortBy, setSortBy] = useState('requestsSubmitted');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const pagination = { page, pageSize, sortBy, sortDir };
  const { data, isLoading, error } = useAeEffectiveness(filters, pagination);

  const handleSort = (newSortBy: string, newSortDir: 'asc' | 'desc') => {
    setSortBy(newSortBy);
    setSortDir(newSortDir);
    setPage(1);
  };

  const columns: ColumnDef<AeMetrics>[] = [
    { key: 'name', header: 'AE Name', sortable: true },
    { key: 'requestsSubmitted', header: 'Requests Submitted', sortable: true, align: 'right' },
    { key: 'requestsCompleted', header: 'Requests Completed', sortable: true, align: 'right' },
    {
      key: 'completionRate',
      header: 'Completion Rate %',
      sortable: true,
      align: 'right',
      render: (row) => row.completionRate != null ? `${row.completionRate.toFixed(1)}%` : '—',
    },
    {
      key: 'exceptionRate',
      header: 'Exception Rate %',
      sortable: true,
      align: 'right',
      render: (row) => row.exceptionRate != null ? `${row.exceptionRate.toFixed(1)}%` : '—',
    },
    {
      key: 'avgTurnaroundBusinessDays',
      header: 'Avg Turnaround',
      sortable: true,
      align: 'right',
      render: (row) => row.avgTurnaroundBusinessDays != null ? `${row.avgTurnaroundBusinessDays.toFixed(1)} days` : '—',
    },
  ];

  return (
    <section className="mb-8">
      <h2 className={SECTION_HEADING}>AE Effectiveness</h2>
      {error ? (
        <div className="mt-4 text-sm text-[#B91C1C]">Failed to load AE effectiveness data</div>
      ) : (
        <div className="mt-4">
          <DrillDownTable<AeMetrics>
            columns={columns}
            data={data?.aes.items ?? []}
            totalCount={data?.aes.totalCount ?? 0}
            page={page}
            pageSize={pageSize}
            sortBy={sortBy}
            sortDir={sortDir}
            onSort={handleSort}
            onPageChange={setPage}
            loading={isLoading}
            rowKey={(row) => row.userId}
            rowClassName={(row) => row.flagged ? 'bg-[#FEE2E2]' : undefined}
            emptyMessage="No AE effectiveness data available"
          />
        </div>
      )}
    </section>
  );
}

// ─── Section 4: Exceptions (Metric 2.4) ────────────────────────────────────

function ExceptionsSection({ filters }: { filters: AnalyticsFilters }) {
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [sortBy, setSortBy] = useState('reviewTimeBusinessDays');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const pagination = { page, pageSize, sortBy, sortDir };
  const { data, isLoading, error } = useExceptions(filters, pagination);

  const handleSort = (newSortBy: string, newSortDir: 'asc' | 'desc') => {
    setSortBy(newSortBy);
    setSortDir(newSortDir);
    setPage(1);
  };

  const columns: ColumnDef<ExceptionDetail>[] = [
    { key: 'requestNumber', header: 'Request #', sortable: true },
    { key: 'displayName', header: 'Display Name', sortable: true },
    { key: 'aeName', header: 'AE Name', sortable: true },
    { key: 'exceptionStatus', header: 'Exception Status', sortable: true },
    {
      key: 'reviewTimeBusinessDays',
      header: 'Review Time',
      sortable: true,
      align: 'right',
      render: (row) => row.reviewTimeBusinessDays != null ? `${row.reviewTimeBusinessDays} days` : '—',
    },
  ];

  return (
    <section className="mb-8">
      <h2 className={SECTION_HEADING}>Exceptions</h2>
      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <KpiCard
          label="Approval Rate"
          value={data?.approvalRate != null ? data.approvalRate.toFixed(1) : null}
          unit="%"
          loading={isLoading}
          error={error ? 'Failed to load exceptions data' : null}
        />
      </div>
      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
        <ChartCard
          title="Exception Volume"
          loading={isLoading}
          error={error ? 'Failed to load exception volume' : null}
        >
          {data?.volume && data.volume.length > 0 ? (
            <BarChartWrapper
              data={data.volume as Record<string, unknown>[]}
              categoryKey="date"
              series={[
                { dataKey: 'count', name: 'Volume', color: '#6B4FBB' },
              ]}
            />
          ) : (
            <div className="flex items-center justify-center h-[300px]">
              <p className="text-sm text-[#555555]">No data available</p>
            </div>
          )}
        </ChartCard>
        <ChartCard
          title="Approval Rate Trend"
          loading={isLoading}
          error={error ? 'Failed to load approval trend' : null}
        >
          {data?.approvalTrend && data.approvalTrend.length > 0 ? (
            <LineChartWrapper
              data={data.approvalTrend as Record<string, unknown>[]}
              xAxisKey="date"
              series={[
                { dataKey: 'rate', name: 'Approval Rate %', color: '#1A7A3C' },
              ]}
              yAxisLabel="Rate %"
              yDomain={[0, 100]}
            />
          ) : (
            <div className="flex items-center justify-center h-[300px]">
              <p className="text-sm text-[#555555]">No data available</p>
            </div>
          )}
        </ChartCard>
      </div>
      <div className="mt-4">
        <DrillDownTable<ExceptionDetail>
          columns={columns}
          data={data?.details.items ?? []}
          totalCount={data?.details.totalCount ?? 0}
          page={page}
          pageSize={pageSize}
          sortBy={sortBy}
          sortDir={sortDir}
          onSort={handleSort}
          onPageChange={setPage}
          loading={isLoading}
          rowKey={(row) => row.id}
          emptyMessage="No exception details available"
        />
      </div>
    </section>
  );
}

// ─── Section 5: Expiring Holds (Metric 2.5) ────────────────────────────────

function ExpiringHoldsSection({ filters }: { filters: AnalyticsFilters }) {
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [sortBy, setSortBy] = useState('daysUntilExpiration');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  const pagination = { page, pageSize, sortBy, sortDir };
  const { data, isLoading, error } = useExpiringHolds(filters, pagination);

  const handleSort = (newSortBy: string, newSortDir: 'asc' | 'desc') => {
    setSortBy(newSortBy);
    setSortDir(newSortDir);
    setPage(1);
  };

  const columns: ColumnDef<ExpiringHoldItem>[] = [
    { key: 'requestNumber', header: 'Request #', sortable: true },
    { key: 'displayName', header: 'Display Name', sortable: true },
    { key: 'aeName', header: 'AE Name', sortable: true },
    { key: 'region', header: 'Region', sortable: true },
    { key: 'businessCategory', header: 'Category', sortable: true },
    {
      key: 'holdExpirationDate',
      header: 'Expiration Date',
      sortable: true,
      render: (row) => formatDate(row.holdExpirationDate),
    },
    { key: 'chartingStatus', header: 'Status', sortable: true },
    {
      key: 'daysUntilExpiration',
      header: 'Days Until Expiration',
      sortable: true,
      align: 'right',
    },
  ];

  return (
    <section className="mb-8">
      <h2 className={SECTION_HEADING}>Expiring Holds</h2>
      <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-6">
        <KpiCard
          label="Expiring in 7 Days"
          value={data?.counts.days7 ?? null}
          loading={isLoading}
          error={error ? 'Failed to load data' : null}
          className="border-l-4 border-l-[#92400E]"
        />
        <KpiCard
          label="Expiring in 14 Days"
          value={data?.counts.days14 ?? null}
          loading={isLoading}
          error={error ? 'Failed to load data' : null}
          className="border-l-4 border-l-[#92400E]"
        />
        <KpiCard
          label="Expiring in 30 Days"
          value={data?.counts.days30 ?? null}
          loading={isLoading}
          error={error ? 'Failed to load data' : null}
          className="border-l-4 border-l-[#92400E]"
        />
      </div>
      <div className="mt-4">
        <DrillDownTable<ExpiringHoldItem>
          columns={columns}
          data={data?.items.items ?? []}
          totalCount={data?.items.totalCount ?? 0}
          page={page}
          pageSize={pageSize}
          sortBy={sortBy}
          sortDir={sortDir}
          onSort={handleSort}
          onPageChange={setPage}
          loading={isLoading}
          rowKey={(row) => row.id}
          emptyMessage="No expiring holds found"
        />
      </div>
    </section>
  );
}

// ─── Section 6: Volume Breakdown (Metric 2.6) ──────────────────────────────

function VolumeBreakdownSection({ filters }: { filters: AnalyticsFilters }) {
  const { data, isLoading, error } = useVolumeBreakdown(filters);

  const donutData = (data?.byRegion ?? []).map((r) => ({
    name: r.region,
    value: r.count,
    color: REGION_COLORS[r.region] ?? '#555555',
  }));

  const categoryData = (data?.byCategory ?? []).map((c) => ({
    category: c.category,
    count: c.count,
  }));

  return (
    <section className="mb-8">
      <h2 className={SECTION_HEADING}>Volume Breakdown</h2>
      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-6">
        <ChartCard
          title="Line Items by Region"
          loading={isLoading}
          error={error ? 'Failed to load region breakdown' : null}
        >
          {donutData.length > 0 ? (
            <DonutChartWrapper data={donutData} />
          ) : (
            <div className="flex items-center justify-center h-[300px]">
              <p className="text-sm text-[#555555]">No data available</p>
            </div>
          )}
        </ChartCard>
        <ChartCard
          title="Hold Requests by Category (Top 10)"
          loading={isLoading}
          error={error ? 'Failed to load category breakdown' : null}
        >
          {categoryData.length > 0 ? (
            <BarChartWrapper
              data={categoryData as Record<string, unknown>[]}
              categoryKey="category"
              horizontal
              height={Math.max(300, categoryData.length * 35)}
              series={[
                { dataKey: 'count', name: 'Requests', color: '#6B4FBB' },
              ]}
            />
          ) : (
            <div className="flex items-center justify-center h-[300px]">
              <p className="text-sm text-[#555555]">No data available</p>
            </div>
          )}
        </ChartCard>
      </div>
    </section>
  );
}

// ─── Main Sales View ────────────────────────────────────────────────────────

/**
 * Sales View — displays all 6 sales metric sections.
 * Each section has independent loading and error handling.
 */
export function SalesView({ filters }: SalesViewProps) {
  return (
    <div>
      <RequestVolumeSection filters={filters} />
      <AeTurnaroundSection filters={filters} />
      <AeEffectivenessSection filters={filters} />
      <ExceptionsSection filters={filters} />
      <ExpiringHoldsSection filters={filters} />
      <VolumeBreakdownSection filters={filters} />
    </div>
  );
}

export default SalesView;
